import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { useCallback } from "react";

// Validation Queries
export const CHECK_WALLET_BALANCE = gql`
  query CheckWalletBalance($tradeId: Int!, $quantity: Int!, $price: Float!) {
    checkWalletBalance(tradeId: $tradeId, quantity: $quantity, price: $price) {
      hasSufficientBalance
      requiredAmount
      currentBalance
      shortfall
      suggestedDeposit
    }
  }
`;

export const GET_TRADE_VALIDATION_SUMMARY = gql`
  query GetTradeValidationSummary($tradeId: Int!) {
    tradeValidationSummary(tradeId: $tradeId) {
      isValid
      errors
      warnings
      suggestedActions
    }
  }
`;

export const IS_TRADE_CLOSED = gql`
  query IsTradeClosed($tradeId: Int!) {
    isTradeClosed(tradeId: $tradeId)
  }
`;

// Position Validation Queries
export const GET_POSITION_VALIDATION_SUMMARY = gql`
  query GetPositionValidationSummary($tradeId: Int!) {
    tradeValidationSummary(tradeId: $tradeId) {
      isValid
      errors
      warnings
      suggestedActions
    }
  }
`;

// Wallet Mutations
export const DEPOSIT_TO_WALLET = gql`
  mutation DepositToWallet(
    $amount: Float!
    $walletId: String!
    $notes: String
  ) {
    createWalletActivity(
      input: {
        walletId: $walletId
        type: DEPOSIT
        amount: $amount
        notes: $notes
      }
    ) {
      id
      amount
      type
      createdAt
    }
  }
`;

// Wallet Balance Query
export const CHECK_WALLET_BALANCE_QUERY = gql`
  query CheckWalletBalance($tradeId: Int!, $quantity: Int!, $price: Float!) {
    checkWalletBalance(tradeId: $tradeId, quantity: $quantity, price: $price) {
      hasSufficientBalance
      requiredAmount
      currentBalance
      shortfall
      suggestedDeposit
    }
  }
`;

// Position Status Mutations
export const UPDATE_POSITION_STATUS = gql`
  mutation UpdatePositionStatus($id: String!, $status: PositionStatus!) {
    updatePosition(id: $id, input: { status: $status }) {
      id
      status
      updatedAt
    }
  }
`;

// Trade Reopening Mutations
export const REOPEN_TRADE = gql`
  mutation ReopenTrade($tradeId: Int!) {
    reopenTrade(tradeId: $tradeId) {
      id
      status
      updatedAt
    }
  }
`;

// Trade Reopening Hook
export const useReopenTrade = () => {
  const [reopenTradeMutation, { loading, error }] = useMutation(REOPEN_TRADE);

  const reopenTrade = useCallback(
    async (tradeId: number) => {
      try {
        const result = await reopenTradeMutation({
          variables: { tradeId },
        });
        return result.data?.reopenTrade;
      } catch (err) {
        throw err;
      }
    },
    [reopenTradeMutation],
  );

  return {
    reopenTrade,
    loading,
    error,
  };
};

// Wallet Deposit Hook
export const useWalletDeposit = () => {
  const [depositMutation, { loading, error }] = useMutation(DEPOSIT_TO_WALLET);

  const depositToWallet = useCallback(
    async (amount: number, walletId: string, notes?: string) => {
      try {
        const result = await depositMutation({
          variables: { amount, walletId, notes },
        });
        return result.data?.createWalletActivity;
      } catch (err) {
        throw err;
      }
    },
    [depositMutation],
  );

  return {
    depositToWallet,
    loading,
    error,
  };
};

// Validation Hooks
export const useCheckWalletBalance = () => {
  const [runBalanceCheck, { data, loading, error }] = useLazyQuery(
    CHECK_WALLET_BALANCE_QUERY,
  );

  const checkWalletBalance = useCallback(
    async (tradeId: number, quantity: number, price: number) => {
      try {
        const result = await runBalanceCheck({
          variables: { tradeId, quantity, price },
        });
        return result.data?.checkWalletBalance;
      } catch (err) {
        throw err;
      }
    },
    [runBalanceCheck],
  );

  return {
    checkWalletBalance,
    loading,
    error,
    data: data?.checkWalletBalance,
  };
};

export const useTradeStatus = () => {
  return {
    checkTradeStatus: async (tradeId: number) => {
      // This would be implemented with Apollo Client
      // For now, return mock data
      return {
        isClosed: Math.random() > 0.5, // Random mock status
      };
    },
  };
};

export const useValidationSummary = () => {
  const [fetchSummary, { data, loading, error }] = useLazyQuery(
    GET_TRADE_VALIDATION_SUMMARY,
  );

  const getValidationSummary = useCallback(
    async (tradeId: number) => {
      try {
        const result = await fetchSummary({ variables: { tradeId } });
        return result.data?.tradeValidationSummary;
      } catch (err) {
        throw err;
      }
    },
    [fetchSummary],
  );

  return {
    getValidationSummary,
    loading,
    error,
    data: data?.tradeValidationSummary,
  };
};
